import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import { LayoutGrid } from "lucide-react-native";
import React, { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import type { AddPlayersSource } from "../../../utils/addPlayersPanelSources";

/** Tab labels. Kept here so the source keys stay internal identifiers. */
const SOURCE_LABEL: Record<AddPlayersSource, string> = {
  suggested: "Suggested",
  watchlist: "Watchlists",
  roster: "My Roster",
};

/**
 * Shared tab geometry (PC1 362:39276).
 *
 * The underline is reserved at 2px on BOTH states so the strip never shifts,
 * but per the PC frames only the SELECTED tab draws a rule — the inactive
 * reservation is transparent, and the strip itself carries the full-width
 * hairline underneath.
 *
 * `min-w-28` (SE-7095): `flex-1` alone splits the strip's width EQUALLY
 * across all three tabs, but only Suggested renders a leading icon — it
 * needs strictly more room than Watchlists/My Roster for the same label+
 * count content. Without a floor, a narrow strip starves Suggested first:
 * its label (`isTruncated`) ellipsizes to "Sugges…" and the count, with no
 * shrink protection of its own, is squeezed to nothing. `min-w-28` (112px,
 * the closed-token snap of the 120px this geometry was tuned for — see the
 * body-strong-at-14px note below) is a hard floor `flex-1`'s shrink cannot
 * cross, so every tab always keeps room for its full label + count.
 */
const TAB_BASE_CLASS =
  "flex-1 min-w-28 flex-row items-center justify-center gap-1 rounded-t-sm border-b-2 px-1 py-1";

/** Selected tab: near-black label over a near-black rule. */
const TAB_ACTIVE_CLASS = "border-b-outline-emphasis";

/** Unselected tab: muted label over a transparent (reserved) rule. */
const TAB_INACTIVE_CLASS = "border-b-transparent";

/*
 * Count presentation (PC1): the number renders as its own MUTED text run
 * after the label — "Suggested 8" with the 8 a step lighter — matching the
 * PC frames. This supersedes the SE-6554-era parenthesised single-run form,
 * which the redesigned frames no longer draw.
 */

/** Props for {@link AddPlayersSourceTabsView}. */
interface AddPlayersSourceTabsViewProps {
  /**
   * The sources THIS pipeline offers, in render order. Already narrowed by the
   * pipeline's motion — this component renders exactly what it is handed and
   * never re-derives the mapping.
   */
  readonly sources: readonly AddPlayersSource[];
  /** Currently selected source. */
  readonly selected: AddPlayersSource;
  /**
   * Per-source select handlers, keyed by source. A precomputed map rather than
   * an `onSelect(source)` callback so the `map` below creates no closures
   * (`react-perf`) — the same shape the review-queue filter chips use.
   */
  readonly handlers: Partial<Record<AddPlayersSource, () => void>>;
  /**
   * Per-source item counts, keyed by source. PARTIAL on purpose: a source whose
   * count the panel cannot state truthfully is omitted and renders a bare
   * label, rather than showing a number that means something narrower than the
   * user will read into it. See the panel container for which sources those are
   * and why.
   */
  readonly counts?: Partial<Record<AddPlayersSource, number>>;
}

/**
 * The add-players panel's contextual source tabs.
 *
 * Note what is NOT here: global search. Search is a persistent field alongside
 * these tabs, not a fourth tab, because it is not a curated pool — it reaches
 * any player regardless of which of these is selected.
 *
 * ## Announced selection (SE-6265)
 *
 * The tabs first shipped with `accessibilityRole="tab"` +
 * `accessibilityState={{ selected }}`. React-native-web maps the modern
 * `role` / `aria-*` props and has NO handler for the legacy
 * `accessibilityState` object, so it was dropped on the floor: measured on
 * dev, all three tabs reached the DOM with no `aria-selected` at all, and a
 * screen reader was told a tablist existed without being told which tab was
 * current.
 *
 * The mechanism below is copied verbatim from the app's own bottom tab bar
 * (expo-router's `BottomTabItem`/`BottomTabBar`, which is why `TAB_BAR.*`
 * emits the attribute correctly in the same DOM snapshot): a `tablist`
 * container wrapping items that carry `role="tab"` and a BOOLEAN
 * `aria-selected`. The boolean matters — the unselected tabs must announce
 * `aria-selected="false"` rather than omitting the attribute, or ARIA treats
 * their state as unannounced too.
 * @param root0 - Component props.
 * @param root0.sources - Sources this pipeline offers.
 * @param root0.selected - Selected source.
 * @param root0.handlers - Per-source select handlers.
 * @param root0.counts - Per-source item counts, where the panel knows one.
 */
const AddPlayersSourceTabsView = ({
  sources,
  selected,
  handlers,
  counts = {},
}: AddPlayersSourceTabsViewProps) => (
  <Stack
    direction="horizontal"
    role="tablist"
    className="items-center border-b border-outline-subtle"
  >
    {sources.map(source => (
      <Pressable
        key={source}
        testID={TEST_ID_ADD_PLAYERS_PANEL.SOURCE_TAB(source)}
        role="tab"
        aria-selected={source === selected}
        onPress={handlers[source]}
        className={`${TAB_BASE_CLASS} ${
          source === selected ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS
        }`}
      >
        {/* PC1 draws a small grid glyph on the Suggested tab only. */}
        {source === "suggested" ? (
          <Icon
            as={LayoutGrid}
            size="xs"
            className={
              source === selected
                ? "text-content-primary"
                : "text-content-secondary"
            }
          />
        ) : null}
        <Text
          /* Weight-only difference at ONE size — the PC frames draw the tab
             labels at 14px (the SE-6554 artboard's 16px no longer fits the
             icon + count row in a 120px tab). `body-strong` bundles 14px +
             the medium face for the selected state; the inactive run drops
             to the regular family. */
          variant={source === selected ? "body-strong" : "body"}
          className={
            source === selected
              ? "text-content-primary"
              : "text-content-secondary"
          }
          isTruncated
        >
          {SOURCE_LABEL[source]}
        </Text>
        {counts[source] === undefined ? null : (
          <Text variant="body" className="text-content-muted">
            {String(counts[source])}
          </Text>
        )}
      </Pressable>
    ))}
  </Stack>
);

AddPlayersSourceTabsView.displayName = "AddPlayersSourceTabsView";

export default memo(AddPlayersSourceTabsView);
